/**
 * Report API client — functions for Financial Report and IT Spend Report endpoints.
 *
 * Requirements: 20.1-20.11, 43.1-43.13
 */

import { apiClient } from '@/services/apiClient';

// ─── Financial Report Types ────────────────────────────────────────────

export interface FinancialReportParams {
  groupBy?: string;
  period?: 'monthly' | 'quarterly' | 'annual';
  vendorId?: number;
  glCode?: string;
  budgetCategory?: string;
  departmentScope?: string;
  contractType?: string;
  dateFrom?: string;
  dateTo?: string;
  riskTier?: string;
  siteNumber?: string;
  includeBudgetOverlay?: boolean;
}

export interface FinancialReportRow {
  vendor?: string;
  glCode?: string;
  budgetCategory?: string;
  departmentScope?: string;
  siteNumber?: string;
  period?: string;
  contractSpend: number;
  invoicedSpend: number;
  budgetedAmount?: number;
  dollarVariance?: number;
  percentVariance?: number | null;
}

export interface FinancialReportResult {
  rows: FinancialReportRow[];
  subtotals: Record<string, number>;
  grandTotal: number;
  grandTotalInvoiced: number;
}

// ─── IT Spend Report Types ─────────────────────────────────────────────

export interface ITSpendReportParams {
  siteNumber?: string;
  division?: string;
  region?: string;
  spendCategory?: 'contracts' | 'circuits' | 'hardware' | 'invoiced';
  period?: 'monthly' | 'quarterly' | 'annual';
  abcCategory?: string;
  includeBudgetOverlay?: boolean;
}

export interface ITSpendSiteRow {
  siteNumber: string;
  branchName: string;
  contractSpend: number;
  circuitSpend: number;
  hardwareSpend: number;
  invoicedSpend: number;
  totalSpend: number;
  abcCategory?: string;
  employeeCount?: number;
  spendPerEmployee?: number;
  budgetedAmount?: number;
  dollarVariance?: number;
}

export interface ITSpendReportResult {
  sites: ITSpendSiteRow[];
  top10: ITSpendSiteRow[];
  bottom10: ITSpendSiteRow[];
  totalContractSpend: number;
  totalCircuitSpend: number;
  totalHardwareSpend: number;
  totalInvoicedSpend: number;
  grandTotal: number;
}

// ─── Service ───────────────────────────────────────────────────────────

export const reportService = {
  /** Fetch financial report with filters and grouping. */
  getFinancialReport(params: FinancialReportParams = {}): Promise<FinancialReportResult> {
    return apiClient.get('reports/financial', params as Record<string, string | number | boolean | undefined>);
  },

  /** Get the CSV export URL for the financial report. */
  getFinancialExportUrl(params: FinancialReportParams = {}): string {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return `/api/reports/financial/export${qs ? `?${qs}` : ''}`;
  },

  /** Get GL code suggestions for a vendor. */
  getGLCodeSuggestions(vendorId: number): Promise<string[]> {
    return apiClient.get('reports/financial/gl-suggestions', { vendorId });
  },

  /** Fetch IT spend report with filters. */
  getITSpendReport(params: ITSpendReportParams = {}): Promise<ITSpendReportResult> {
    return apiClient.get('reports/it-spend', params as Record<string, string | number | boolean | undefined>);
  },

  /** Get the CSV export URL for the IT spend report. */
  getITSpendExportUrl(params: ITSpendReportParams = {}): string {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') {
        searchParams.set(key, String(value));
      }
    }
    const qs = searchParams.toString();
    return `/api/reports/it-spend/export${qs ? `?${qs}` : ''}`;
  },
};
